import { useEffect, useMemo, useRef, useState } from 'react';
import ActionStatusText from '../../components/ui/ActionStatusText';
import ErrorMessage from '../../components/ui/ErrorMessage';
import { exerciseEditorUtils } from '../../helpers';
import { isExerciseFormIncomplete } from '../../helpers/domain';
import useUpdateExercise from '../../hooks/data/exercises/useUpdateExercise';
import useExerciseForm from '../../hooks/exercise/useExerciseForm';
import ExerciseTypeSearch from '../components/ExerciseTypeSearch';
import ModalHeader from '../components/ModalHeader';

const ModalExerciseEditor = ({ exercise, currentDay, onClose, onGoBack }) => {
    const isCustom = Number(exercise?.type) === 3;
    const isSteps = Number(exercise?.type) === 2;

    const { normalizeStandard, normalizeSteps, normalizeCustom, buildExercisePayload } = exerciseEditorUtils;

    const initialRef = useRef(null);
    const initialTypeIdRef = useRef(null);

    const {
        form,
        handleChange,
        handleSelect,
        clearSearch,
        filteredResults,
        exerciseTypes,
        dropdownRef,
        showSuggestions,
        showInvalidMessage,
        resetForm,
        isLoading: isLoadingTypes,
        isError: isErrorTypes
    } = useExerciseForm();

    const { updateExercise, isUpdating, isSuccess, isError, error } = useUpdateExercise(currentDay);

    const [customData, setCustomData] = useState({
        name: '',
        duration: '',
        kcal: '',
        time: '',
        comment: ''
    });

    // INIT

    useEffect(() => {
        if (!exercise) return;

        const type = Number(exercise.type);

        if (type === 3) {
            const initial = {
                name: exercise.name ?? '',
                duration: exercise.duration ?? '',
                kcal: exercise.kcal ?? '',
                time: exercise.time ?? '',
                comment: exercise.comment ?? ''
            };

            setCustomData(initial);
            initialRef.current = initial;
            initialTypeIdRef.current = null;
            return;
        }

        const initial = {
            exercise_type: type,
            exercise_id: exercise.exercise_id ?? '',
            exercise_name: type === 2 ? 'Stegräknare' : exercise.name ?? '',
            exercise_duration: exercise.duration ?? '',
            exercise_steps: exercise.steps ?? '',
            exercise_time: exercise.time ?? '',
            exercise_comment: exercise.comment ?? ''
        };

        resetForm(initial);
        initialRef.current = initial;
        initialTypeIdRef.current = exercise.exercise_id;
    }, [exercise, resetForm]);

    const isLocked = isUpdating || isSuccess || isLoadingTypes;

    // CHANGE DETECTION

    const isDifferent = (a, b, normalizer) => JSON.stringify(normalizer(a)) !== JSON.stringify(normalizer(b));

    const hasChanged = (() => {
        if (!initialRef.current) return false;

        if (isCustom) return isDifferent(customData, initialRef.current, normalizeCustom);

        if (isSteps) return isDifferent(form, initialRef.current, normalizeSteps);

        return (
            isDifferent(form, initialRef.current, normalizeStandard) ||
            Number(form.exercise_id) !== Number(initialTypeIdRef.current)
        );
    })();

    // VALIDATION

    const isCustomFormIncomplete = !customData.kcal || !customData.time;

    const isFormIncomplete = useMemo(
        () => (isCustom ? isCustomFormIncomplete : isExerciseFormIncomplete(form, exerciseTypes)),
        [isCustom, isCustomFormIncomplete, form, exerciseTypes]
    );

    // SUBMIT

    const handleSubmit = async () => {
        try {
            const payload = buildExercisePayload({ isCustom, form, customData });
            await updateExercise(exercise.id, payload);
        } catch (err) {
            console.error('Failed to update exercise:', err);
        }
    };

    return (
        <>
            <ModalHeader
                title="Redigera motion"
                onClose={onClose}
                onGoBack={onGoBack}
                disableButtons={isUpdating || isLoadingTypes}
            />

            <div className="modal-body exercise">
                {isError || isErrorTypes ? (
                    <ErrorMessage
                        message={error?.message || 'Ett fel inträffade vid uppdateringen. Försök igen.'}
                    />
                ) : (
                    <div className="exercise-input-wrapper" ref={dropdownRef}>
                        {!isCustom && !isSteps && (
                            <ExerciseTypeSearch
                                form={form}
                                filteredResults={filteredResults}
                                showSuggestions={showSuggestions}
                                showInvalidMessage={showInvalidMessage}
                                handleChange={handleChange}
                                handleSelect={handleSelect}
                                clearSearch={clearSearch}
                                isLocked={isLocked}
                                mode="edit"
                            />
                        )}

                        {isCustom && (
                            <div className="input-with-label">
                                <span className="inline-label">Namn:</span>
                                <input
                                    type="text"
                                    className="modal-input name-input"
                                    value={customData.name}
                                    onChange={(e) => setCustomData((p) => ({ ...p, name: e.target.value }))}
                                    disabled={isLocked}
                                />
                            </div>
                        )}

                        {isSteps && <span className="steps-label">Stegräknare</span>}

                        {isSteps ? (
                            <div className="input-with-label">
                                <span className="inline-label">Antal steg:</span>
                                <input
                                    type="number"
                                    name="exercise_steps"
                                    className="modal-input steps-input"
                                    min="1"
                                    step="1"
                                    value={form.exercise_steps ?? ''}
                                    onChange={(e) => handleChange(e)}
                                    disabled={isLocked}
                                />
                            </div>
                        ) : (
                            <div className="input-with-label">
                                <span className="inline-label">Tid (min):</span>
                                <input
                                    type="number"
                                    className="modal-input duration-input"
                                    min="1"
                                    step="1"
                                    value={isCustom ? customData.duration : form.exercise_duration}
                                    onChange={(e) =>
                                        isCustom
                                            ? setCustomData((p) => ({ ...p, duration: e.target.value }))
                                            : handleChange(e)
                                    }
                                    name={isCustom ? undefined : 'exercise_duration'}
                                    disabled={isLocked}
                                />
                            </div>
                        )}

                        {isCustom && (
                            <div className="input-with-label">
                                <span className="inline-label">Kcal:</span>
                                <input
                                    type="number"
                                    className="modal-input kcal-input"
                                    min="0"
                                    step="any"
                                    value={customData.kcal}
                                    onChange={(e) => setCustomData((p) => ({ ...p, kcal: e.target.value }))}
                                    disabled={isLocked}
                                />
                            </div>
                        )}

                        <div className="input-with-label">
                            <span className="inline-label">Klockslag:</span>
                            <input
                                type="time"
                                className="modal-input time-input"
                                value={isCustom ? customData.time : form.exercise_time}
                                onChange={(e) =>
                                    isCustom
                                        ? setCustomData((p) => ({ ...p, time: e.target.value }))
                                        : handleChange(e)
                                }
                                name={isCustom ? undefined : 'exercise_time'}
                                disabled={isLocked}
                            />
                        </div>

                        <div className="input-with-label">
                            <span className="inline-label">Kommentar:</span>
                            <input
                                type="text"
                                className="modal-input comment-input"
                                value={isCustom ? customData.comment : form.exercise_comment}
                                onChange={(e) =>
                                    isCustom
                                        ? setCustomData((p) => ({ ...p, comment: e.target.value }))
                                        : handleChange(e)
                                }
                                name={isCustom ? undefined : 'exercise_comment'}
                                disabled={isLocked}
                                placeholder="Valfritt"
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="modal-footer exercise">
                <button
                    type="button"
                    className="btn-green bold-title"
                    disabled={isLocked || isFormIncomplete || !hasChanged || isLoadingTypes}
                    onClick={handleSubmit}
                >
                    <ActionStatusText
                        defaultLabel="Spara ändringar"
                        successLabel="Ändringar har sparats!"
                        isProcessing={isUpdating}
                        isCompleted={isSuccess}
                    />
                </button>

                <button
                    type="button"
                    className="marked-border-btn bold-title"
                    disabled={isUpdating || isLoadingTypes}
                    onClick={onGoBack}
                >
                    Gå tillbaka
                </button>
            </div>
        </>
    );
};

export default ModalExerciseEditor;
